// "Needs you today" on the coach dashboard (item 12): one list of the clients who need the coach,
// and why. Pure, so it can be tested without a database; hooks/useNeedsYou.js loads the inputs.
// Since 15 October 2026 it lives in People, and a coach can Clear a client (migration 079).
import { statusGivesAccess } from './paymentAccess';

// A paying client who has not trained for longer than this is on the list.
export const NOT_TRAINED_DAYS = 7;
// Only check-ins this recent count as waiting for a review; older unreviewed ones are history.
export const CHECK_IN_WINDOW_DAYS = 28;
// A client who joined this recently, and has had no message from the coach, is a new client to greet.
export const NEW_CLIENT_DAYS = 7;
// After a Clear, "no workout" stays hidden this long (from the Clear), if they still have not trained.
export const IDLE_CLEAR_DAYS = 14;

const plural = (n, one, many) => `${n} ${n === 1 ? one : many}`;
// A count, or { count, newest } from the loaders that also give the newest event's time
const countOf = (v) => (typeof v === 'number' ? v : v?.count || 0);
const newestOf = (v) => (v && typeof v === 'object' ? v.newest || null : null);
// Shown again after a Clear only if something is newer than what the coach was shown. Without a
// time to compare (an old loader, or a Clear made with nothing of that kind showing), it shows.
const newerThanSeen = (newest, seen) => !seen || !newest || new Date(newest) > new Date(seen);

// May this client train now? Clients without access cannot open their program, so "has not
// trained" says nothing about them.
// past_due: a failed card payment keeps access while Stripe retries (owner, 24 October 2026).
export const hasTrainingAccess = (client = {}) =>
  !!client.is_complimentary || statusGivesAccess(client.subscription_status);

/**
 * @param {Object} input
 * @param {Array} input.clients - users rows (auth_user_id, name, status, subscription_status, is_complimentary)
 * @param {Object} input.stats - by auth_user_id: { daysSinceLast, flagCount }
 * @param {Object} input.unreadMessages - by auth_user_id: messages from the client the coach has not read
 * @param {Object} input.checkInsWaiting - by auth_user_id: recent check-ins with no Coach Review
 * @param {Object} input.coachHasWritten - by auth_user_id: true once the coach has sent a message
 * @param {Object} input.clears - by auth_user_id: the newest Clear (migration 079):
 *   { cleared_at, seen_message_at, seen_check_in_at, seen_flag_at }
 * @param {Date} input.now
 * @returns {Array<{ client: Object, reasons: Array<{ kind: string, text: string }> }>}
 *   most urgent first: messages, then new clients, then check-ins, then flags, then no training.
 */
export function needsYouToday({ clients = [], stats = {}, unreadMessages = {}, checkInsWaiting = {}, coachHasWritten = {}, clears = {}, now = new Date() }) {
  const rows = [];
  for (const client of clients) {
    if (client.status && client.status !== 'active') continue;
    const uid = client.auth_user_id;
    const s = stats[uid] || {};
    const cleared = clears[uid] || null;
    const reasons = [];

    const unread = countOf(unreadMessages[uid]);
    if (unread > 0 && (!cleared || newerThanSeen(newestOf(unreadMessages[uid]), cleared.seen_message_at))) {
      reasons.push({ kind: 'message', text: plural(unread, 'unread message', 'unread messages') });
    }

    const joined = client.created_at ? new Date(client.created_at) : null;
    const daysSinceJoining = joined && !isNaN(joined) ? Math.floor((now - joined) / 86400000) : null;
    const isNew = daysSinceJoining !== null && daysSinceJoining >= 0 && daysSinceJoining <= NEW_CLIENT_DAYS;
    if (isNew && !coachHasWritten[uid] && !cleared) {
      reasons.push({ kind: 'new', text: daysSinceJoining === 0 ? 'New client, joined today' : `New client, joined ${plural(daysSinceJoining, 'day', 'days')} ago` });
    }

    const checkIns = countOf(checkInsWaiting[uid]);
    if (checkIns > 0 && (!cleared || newerThanSeen(newestOf(checkInsWaiting[uid]), cleared.seen_check_in_at))) {
      reasons.push({ kind: 'check-in', text: checkIns === 1 ? 'Check-in waiting for your review' : `${checkIns} check-ins waiting for your review` });
    }

    if (s.flagCount > 0 && (!cleared || newerThanSeen(s.newestFlagAt, cleared.seen_flag_at))) {
      reasons.push({ kind: 'flag', text: plural(s.flagCount, 'flagged exercise', 'flagged exercises') });
    }

    const idleHidden = cleared && cleared.cleared_at
      && (now - new Date(cleared.cleared_at)) < IDLE_CLEAR_DAYS * 86400000;
    if (hasTrainingAccess(client) && !idleHidden) {
      if (s.daysSinceLast === null || s.daysSinceLast === undefined) {
        // stats missing altogether (the activity load failed) is not "never trained";
        // nor is a client in their first week, who is only just starting
        if (stats[uid] && !isNew) reasons.push({ kind: 'idle', text: 'No workout logged yet' });
      } else if (s.daysSinceLast > NOT_TRAINED_DAYS) {
        reasons.push({ kind: 'idle', text: `No workout for ${s.daysSinceLast} days` });
      }
    }

    if (reasons.length) rows.push({ client, reasons });
  }

  const rank = { message: 0, new: 1, 'check-in': 2, flag: 3, idle: 4 };
  const urgency = (row) => Math.min(...row.reasons.map((r) => rank[r.kind]));
  return rows.sort((a, b) => urgency(a) - urgency(b)
    || b.reasons.length - a.reasons.length
    || (a.client.name || '').localeCompare(b.client.name || ''));
}

// What a Clear records: the newest message, check-in and flag this client's card was built from,
// so that anything newer brings them back (plan 004, Codex's finding M1).
export function seenForClear({ uid, stats = {}, unreadMessages = {}, checkInsWaiting = {} }) {
  return {
    seen_message_at: newestOf(unreadMessages[uid]),
    seen_check_in_at: newestOf(checkInsWaiting[uid]),
    seen_flag_at: stats[uid]?.newestFlagAt || null,
  };
}

// The client's tab that holds the job: messages to answer (or a new client to welcome), or a
// check-in to review. Anything else opens the client as before.
export const tabForReasons = (reasons) => {
  const kinds = reasons.map((r) => r.kind);
  if (kinds.includes('message')) return 'messages';
  if (kinds.includes('check-in')) return 'checkins';
  if (kinds.includes('new')) return 'messages';
  return undefined;
};
